import React, { useEffect, useRef, useState } from 'react';

export const MultiSelectCheckbox = ({
  options = [],
  value = [],
  onChange,
  placeholder = 'Select',
  disabled = false,
  selectClassname = '',
  testId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedValues = Array.isArray(value) ? value : [];
  const selectedOptions = (options || []).filter((o) => selectedValues.includes(o.value));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (val) => {
    if (disabled) return;
    const set = new Set(selectedValues);
    if (set.has(val)) {
      set.delete(val);
    } else {
      set.add(val);
    }
    const next = Array.from(set);
    onChange?.(next);
  };

  const handleClear = () => {
    if (disabled) return;
    onChange?.([]);
  };

  const renderButtonLabel = () => {
    if (selectedOptions.length > 0) {
      return selectedOptions.map((opt) => opt.label).join(', ');
    }
    return placeholder;
  };

  return (
    <div className="relative w-full" ref={dropdownRef} data-testid={testId}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex justify-between disabled:!bg-gray-200 items-center bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg outline-none w-full p-2.5 ${selectClassname || ''}`}
      >
        <span className="truncate" title={renderButtonLabel()}>
          {renderButtonLabel()}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="w-5 h-5"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-20 w-full mt-2 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="max-h-56 overflow-y-auto p-2">
            {(options || []).length === 0 && (
              <div className="px-2 py-2 text-sm text-gray-500">No options</div>
            )}

            {(options || []).map((option) => {
              const checked = selectedValues.includes(option.value);
              return (
                <label
                  key={option.value}
                  className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer rounded hover:bg-gray-100 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => handleToggle(option.value)}
                  />
                  <span className="text-sm text-gray-800">{option.label}</span>
                </label>
              );
            })}
          </div>

          <div className="flex items-center justify-between border-t border-gray-200 p-2">
            <div className="text-xs text-gray-600">
              {selectedValues.length} selected
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="text-xs text-gray-700 underline disabled:text-gray-400"
                onClick={handleClear}
                disabled={disabled || selectedValues.length === 0}
              >
                Clear
              </button>
              <button
                type="button"
                className="text-xs text-blue-700 underline"
                onClick={() => setIsOpen(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectCheckbox;


